import AvatarMention from "@/components/communications-chat-components/AvatarMention";
import { generateMentionNodes, matchMentions } from "@/components/communications-chat-components/chatMentionsUtils";
import UserAvatar from "@/components/lazo-ai-chat-components/Avatar";
import LazoModal from "@/components/utils/LazoModal";
import { Colors } from "@/enums/Colors";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { LazoFile } from "@/models/LazoFile";
import { LazoUser } from "@/models/LazoUser";
import { ChatMessageFragmentType } from "@/models/ai-chat/ChatMessageFragment";
import { LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoFileService from "@/services/LazoFileService";
import { Box, BoxProps, Divider, SxProps, Typography, TypographyProps, dividerClasses, styled, typographyClasses } from "@mui/material";
import Linkify from "linkify-react";
import { marked } from "marked";
import moment from "moment";
import React, { CSSProperties, Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileContainer } from "./FileContainer";
import { PageFetchFn } from "./one/OneChatContext";
import { LoadingIcon } from "./portal/PortalChatList";
import ChatSettingsComponent from "./settings-components/ChatSettingsComponent";

function ChatMessage({
  lazoMessage,
  content,
  currentUser,
  dateNow,
}: {
  lazoMessage: LazoChatMessage;
  content: string | React.ReactNode[] | React.ReactNode;
  currentUser: (m: LazoChatMessage) => boolean;
  dateNow: Date;
}) {
  return (
    <MessageContainer sx={{ width: "100%" }}>
      <StyledBoxMessage className={currentUser(lazoMessage) ? "sender" : "received"}>
        <Linkify options={{ target: "_blank" }}>
          <MessageTypographyContainer sender={currentUser(lazoMessage)}>{content}</MessageTypographyContainer>
        </Linkify>
      </StyledBoxMessage>
      <StyledDate className={currentUser(lazoMessage) ? "sender" : "received"}>
        {moment(lazoMessage.createdAt).isSame(dateNow, "day")
          ? moment(lazoMessage.createdAt).fromNow()
          : moment(lazoMessage.createdAt).calendar(dateNow)}
      </StyledDate>
    </MessageContainer>
  );
}

export function ChatMessageList({
  conversation,
  openSettings,
  setOpenSettings,
  onChatSelect,
  setChats,
  messages,
  setMessages,
  members,
  setMembers,
  messagesLoadingPage,
  fetchMessagesPage,
}: {
  conversation: LazoChatConversationShell;
  openSettings: boolean;
  setOpenSettings: Dispatch<SetStateAction<boolean>>;
  onChatSelect: (chat: LazoChatConversationShell) => void;
  setChats: Dispatch<SetStateAction<LazoChatConversationShell[]>>;
  messages: LazoChatMessage[];
  setMessages: Dispatch<SetStateAction<LazoChatMessage[]>>;
  members: SmallConversationMember[];
  setMembers: Dispatch<SetStateAction<SmallConversationMember[]>>;
  messagesLoadingPage: boolean;
  fetchMessagesPage: PageFetchFn;
}) {
  const { t } = useTranslation("chat");
  const currentUserExternalId = AuthService.getUser()?.externalId;

  const currentUser = (message: LazoChatMessage) => {
    const fragType = message.messageFragments?.[0];
    // Is a special system message (not a user-sent file attachment)
    if (fragType && fragType.type !== ChatMessageFragmentType.FILE) {
      return true;
    }
    return message.user?.externalId === currentUserExternalId;
  };

  const [lastMessageExternalId, setLastMessageExternalId] = useState<string>(null);

  const topMessageRef = useRef<HTMLDivElement>(null);
  const messageListContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // On message send or first load scroll to last message
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView();
      setLastMessageExternalId(messages[messages.length - 1]?.externalId || null);
    }
  }, [messages[messages.length - 1]?.externalId !== lastMessageExternalId]);

  // After page was fetched scroll to middle so we don't
  // fetch all
  useEffect(() => {
    if (!messagesLoadingPage && messageListContainerRef.current) {
      messageListContainerRef.current.scrollTo(0, messageListContainerRef.current.getClientRects()[0].bottom);
    }
  }, [messagesLoadingPage]);

  // Setup top observer for page fetching
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchMessagesPage();
          if (topMessageRef.current) {
            observer.unobserve(topMessageRef.current);
          }
        }
      },
      { threshold: 1.0 }
    );

    if (topMessageRef.current) {
      observer.observe(topMessageRef.current);
    }

    return () => {
      if (topMessageRef.current) {
        observer.unobserve(topMessageRef.current);
      }
    };
  }, [messages]);

  const dateNow = new Date();

  let firstDayMessage = false;

  //to render the divider
  const isFirstMessageFromToday = (message: LazoChatMessage, index: number) => {
    const messageDate = message.createdAt ? new Date(message.createdAt) : new Date();

    messageDate.setHours(0, 0, 0, 0);
    dateNow.setHours(0, 0, 0, 0);
    const isSameDay =
      messageDate.getDate() === dateNow.getDate() &&
      messageDate.getMonth() === dateNow.getMonth() &&
      messageDate.getFullYear() === dateNow.getFullYear();

    if (isSameDay) {
      firstDayMessage = true;
      return isSameDay && index;
    }
    return;
  };

  // Check for mentions, and message types. This could be slow with a bunch of messages.
  // We need to start batching them and add infinite scrolling
  function renderMessage(lazoMessage: LazoChatMessage) {
    const fragment = lazoMessage.messageFragments?.[0];
    if (fragment) {
      switch (fragment.type) {
        case ChatMessageFragmentType.FINALIZED_THREAD:
        case ChatMessageFragmentType.SILENT_MEMBER_REMOVED: {
          return <></>;
        }
        case ChatMessageFragmentType.INVITED_USERS_ARRAY: {
          const addedUsers = fragment.content as string[];
          if (addedUsers.length > 1) {
            return (
              <SystemMessageTypography>
                {addedUsers[0]} {t("messageList.systemMessages.userJoined")} {addedUsers.length - 1}{" "}
                {t("messageList.systemMessages.othersJoined")}
              </SystemMessageTypography>
            );
          } else {
            return (
              <SystemMessageTypography>
                {addedUsers[0]} {t("messageList.systemMessages.userAdded")}
              </SystemMessageTypography>
            );
          }
        }
        case ChatMessageFragmentType.REMOVED_USERS_ARRAY: {
          const removedUsers = fragment.content as string[];
          if (removedUsers.length > 1) {
            return (
              <SystemMessageTypography>
                {removedUsers[0]} {t("messageList.systemMessages.userRemoved")} {removedUsers.length - 1}{" "}
                {t("messageList.systemMessages.othersRemoved")}
              </SystemMessageTypography>
            );
          } else {
            return (
              <SystemMessageTypography>
                {removedUsers[0]} {t("messageList.systemMessages.userRemovedSingle")}
              </SystemMessageTypography>
            );
          }
        }
        case ChatMessageFragmentType.CHAT_MEMBER_LEFT: {
          const text = lazoMessage.message;
          const matchedMentions = matchMentions(text);
          const nodes = generateMentionNodes(text, matchedMentions, ChatMessageMentionSx);
          return <SystemMessageTypography>{nodes}</SystemMessageTypography>;
        }
        case ChatMessageFragmentType.CHAT_BEGINNING: {
          const text = lazoMessage.message;
          const matchedMentions = matchMentions(text);
          const nodes = generateMentionNodes(text, matchedMentions, ChatMessageMentionSx);
          const users = fragment.content ? (fragment.content as LazoUser[]) : [];
          return (
            <ConversationBeginningContainer>
              <ConversationBeginningCardsContainer>
                {users.map((lu) => (
                  <AvatarMention
                    key={lu.user.externalId}
                    user={
                      {
                        firstName: lu.user.firstName,
                        lastName: lu.user.lastName,
                      } as User
                    }
                    userExternalId={lu.user.externalId}
                  />
                ))}
              </ConversationBeginningCardsContainer>
              <ConversationBeginningTypography>{nodes}</ConversationBeginningTypography>
            </ConversationBeginningContainer>
          );
        }
        default:
          break;
      }
    } else {
      const text = lazoMessage.message;
      const matchedMentions = matchMentions(text);
      const nodes = generateMentionNodes(text, matchedMentions, ChatMessageMentionSx);
      if (nodes && nodes.length > 0) {
        return <ChatMessage lazoMessage={lazoMessage} content={nodes} currentUser={currentUser} dateNow={dateNow} />;
      }
    }

    return (
      <ChatMessage
        content={<Box dangerouslySetInnerHTML={{ __html: marked.parse(lazoMessage.message) }} />}
        lazoMessage={lazoMessage}
        currentUser={currentUser}
        dateNow={dateNow}
      />
    );
  }

  function onDownloadFile(f: LazoFile): Promise<void> {
    return LazoFileService.downloadFile(f.externalId, f.name);
  }

  return (
    <>
      <ContentContainer>
        <MessageListContainer ref={messageListContainerRef}>
          <LoadingIcon loading={messagesLoadingPage} rect={{ top: 80 }} />
          {messages.map((message, index) => (
            <>
              {!firstDayMessage && (
                <StyledDivider className={isFirstMessageFromToday(message, index) ? "visible" : "no-visible"}>
                  <Typography>{t("messageList.today")}</Typography>
                </StyledDivider>
              )}
              <AvatarAndMessageContainer key={index} ref={index === 0 ? topMessageRef : null}>
                {!currentUser(message) && (
                  <AvatarContainer>
                    {<UserAvatar sx={UserAvatarSx} user={message.user} />}
                    <AvatarNameTypography>{message.user?.firstName + " " + message.user?.lastName}</AvatarNameTypography>
                  </AvatarContainer>
                )}
                {
                  <>
                    {message.message !== "File attachment" && renderMessage(message)}
                    {message.files && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                          maxWidth: "75%",
                          alignSelf: currentUser(message) ? "flex-end" : "flex-start",
                          justifyContent: currentUser(message) ? "flex-end" : "flex-start",
                        }}
                      >
                        {message.files.map((f, idx) => {
                          return (
                            <FileContainer
                              key={idx}
                              file={f}
                              idx={idx}
                              hideDelete
                              lazoFile={f}
                              onDownload={onDownloadFile}
                              showPreview={false}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </>
                }
              </AvatarAndMessageContainer>
            </>
          ))}
          <div ref={bottomRef} />
        </MessageListContainer>
      </ContentContainer>
      <LazoModal title={conversation?.title} open={openSettings} handleClose={() => setOpenSettings(false)} sx={LazoModalChatSettingsSx}>
        <ChatSettingsComponent
          currentUserExternalId={currentUserExternalId}
          conversation={conversation}
          handleClose={() => setOpenSettings(false)}
          onChatSelect={onChatSelect}
          setChats={setChats}
          currentMembers={members}
          setCurrentMembers={setMembers}
          setMessages={setMessages}
        />
      </LazoModal>
    </>
  );
}

const ContentContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  minHeight: 0,
  flex: 1,
  justifyContent: "space-between",
  overflowY: "auto",
  overflowX: "hidden",

  backgroundColor: Colors.ACCENT_F1F3FF,

  "&::-webkit-scrollbar": {
    width: "0.4rem",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  },
}));

const MessageContainer = styled(Box)(() => ({
  gap: "0.5rem",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  height: "100%",
  overflowY: "auto",
  //  paddingTop: "2.5rem",
  "&::-webkit-scrollbar": {
    width: "0.4rem",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  },
}));

const AvatarContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
}));

const StyledDivider = styled(Divider)(() => ({
  width: "100%",
  textAlign: "center",
  lineHeight: "0.1em",
  paddingTop: "1rem",
  [`& .${dividerClasses.wrapper}`]: {
    padding: 0,
  },
  "&::before, &::after": {
    content: '""',
    background: Colors.LIGHT_GRAY_DEDEE1,
    display: "inline-block",
    height: "1px",
    position: "relative",
    verticalAlign: "middle",
    width: "50%",
  },
  [`& .${typographyClasses.root}`]: {
    background: "#fff",
    display: "inline-block",
    borderRadius: "0.375rem",
    border: `1px solid ${Colors.STROKE_DARK_GRAY}`,
    padding: "0 0.5rem",
    position: "relative",
    verticalAlign: "middle",
    fontSize: "0.875rem",
    fontWeight: 400,
  },
  "&.no-visible": {
    display: "none",
  },
  "&.visible": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const StyledDate = styled(Typography)(() => ({
  display: "block",
  fontSize: "0.75rem",
  color: Colors.LIGHT_GRAY_7A7E82,
  "&.received": {
    textAlign: "left",
  },
  "&.sender": {
    textAlign: "right",
  },
}));

const MessageListContainer = styled(Box)(() => ({
  position: "relative",
  overflowY: "auto",
  backgroundColor: Colors.ACCENT_F1F3FF,
  flex: 1,
  minHeight: 0,
  width: "100%",
  paddingBottom: "1rem",
}));

const StyledBoxMessage = styled(Box)(() => ({
  maxWidth: "75%",
  height: "100%",
  wordWrap: "break-word",
  flex: "1",
  padding: "0.5rem 0.75rem",

  "&.received": {
    backgroundColor: Colors.WHITE,
    color: Colors.BLACK,
    alignSelf: "flex-start",
    borderRadius: "0.75rem 0.75rem 0.75rem 0",
  },

  "&.sender": {
    backgroundColor: Colors.BLUE_4763F4,
    color: Colors.WHITE,
    alignSelf: "flex-end",
    borderRadius: "0.75rem 0.75rem 0 0.75rem",
  },
}));

const MessageTypographyContainer = styled(Box)(({ sender }: TypographyProps & { sender: boolean }) => ({
  a: {
    color: sender ? Colors.WHITE : Colors.BLACK,
  },
  "ol, li, p": {
    margin: 0,
    alignItems: "center",
    wordBreak: "break-word",
    flexWrap: "wrap",
    whiteSpace: "pre-line",
    fontWeight: 400,
    fontSize: "0.875rem",
  },
}));

const SystemMessageTypography = styled(Typography)(() => ({
  alignItems: "center",
  display: "flex",
  whiteSpace: "pre",
  flexWrap: "wrap",
  fontWeight: 400,
  fontSize: "0.875rem",
  color: Colors.DARKER_DARKER_GRAY,
}));

const AvatarNameTypography = styled(Typography)(() => ({
  fontWeight: "600",
  fontSize: "0.75rem",
}));

const AvatarAndMessageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "1rem 1rem 0 1.5rem",
  justifyContent: "space-between",
  gap: "0.5rem",
}));

const UserAvatarSx: SxProps = {
  borderRadius: "0.5rem",
  backgroundColor: "#fff",
  backgroundImage: "none",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  color: Colors.ELEGANT_BLACK,
  width: "32px",
  height: "32px",
  fontWeight: "600",
  fontSize: "0.875rem",
};

const ChatMessageMentionSx: BoxProps["sx"] = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: Colors.BLACK_151517,
  padding: "0rem 0.25rem",
};

const LazoModalChatSettingsSx = {
  "&.modal-container": {
    padding: "1.5rem 0",
  },
  "& .close-icon": {
    paddingRight: "1.5rem",
  },
  "& .title-container": {
    justifyContent: "start",
    padding: "0 1.5rem",
    fontSize: "0.875rem",
    fontWeight: 600,
  },
};

const ConversationBeginningContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
}));

const ConversationBeginningCardsContainer = styled(Box)(() => ({
  display: "flex",
  gap: "0.5rem",
}));

const ConversationBeginningTypography = styled(Typography)(() => ({
  display: "flex",
  fontSize: "0.875rem",
  fontWeight: 400,
  gap: "0.25rem",
  backgroundColor: "transparent",
}));

export const PreviewContainerStyle: CSSProperties = {
  width: "12rem",
  maxWidth: "12rem",
  height: "12rem",
  maxHeight: "12rem",
};

export default ChatMessageList;
